import {
  PRODUCT_HOLDING_STATUS_LABELS,
  formatNumber,
  type MapCellDto,
} from '@nova/shared';
import { geoConicConformal, geoPath } from 'd3-geo';
import type { FeatureCollection, Geometry } from 'geojson';
import { Minus, Plus, RotateCcw } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { cn } from '../../lib/cn.js';
import { EMPTY_FILL, MAP_LEGEND, MAP_STROKE, SELECTED_STROKE, STATUS_FILL } from './mapColors.js';
import { kommuneCode, useKommuner, type KommuneFeature, type KommuneProps } from './geo/useKommuner.js';

const W = 460;
const H = 600;
const MIN_K = 1;
const MAX_K = 10;

interface Transform {
  k: number;
  x: number;
  y: number;
}

const IDENTITY: Transform = { k: 1, x: 0, y: 0 };

/** Keep the scaled content covering the viewBox (no panning into empty space). */
function clampTransform(t: Transform): Transform {
  const k = Math.max(MIN_K, Math.min(MAX_K, t.k));
  const x = Math.max(W * (1 - k), Math.min(0, t.x));
  const y = Math.max(H * (1 - k), Math.min(0, t.y));
  return { k, x, y };
}

/** Zoom toward a focal point (in viewBox coords), keeping it visually fixed. */
function zoomAround(t: Transform, factor: number, fx: number, fy: number): Transform {
  const k = Math.max(MIN_K, Math.min(MAX_K, t.k * factor));
  const ratio = k / t.k;
  return clampTransform({ k, x: fx - (fx - t.x) * ratio, y: fy - (fy - t.y) * ratio });
}

/** A pointer event's position in viewBox (0..W, 0..H) coordinates. */
function pointInSvg(e: { clientX: number; clientY: number }, svg: SVGSVGElement): { x: number; y: number } {
  const box = svg.getBoundingClientRect();
  return {
    x: ((e.clientX - box.left) / box.width) * W,
    y: ((e.clientY - box.top) / box.height) * H,
  };
}

/**
 * Interactive Norway municipality choropleth (SVG, no map tiles). Every kommune is
 * a cell: coloured by the dominant status of its matching holdings, or a neutral
 * «ingen data» fill when nothing matches the current filter. Hovering shows the
 * kommune and its holdings, clicking opens the organisation. Supports zoom (scroll
 * / +/− buttons) and pan (drag) over all ~357 kommuner. A conic-conformal
 * projection (suited to Norway) is fitted to the geometry once.
 */
export function NorwayMap({
  cells,
  selectedId,
  onSelect,
}: {
  cells: MapCellDto[];
  selectedId?: string | null;
  onSelect: (cell: MapCellDto) => void;
}) {
  const { data: features, isLoading, isError } = useKommuner();
  const [hover, setHover] = useState<{ code: string; x: number; y: number } | null>(null);
  const [transform, setTransform] = useState<Transform>(IDENTITY);

  // Drag state (refs so we don't re-render per pointermove); a small movement
  // threshold distinguishes a pan from a click so dragging never opens a kommune.
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  const byCode = useMemo(() => {
    const m = new Map<string, MapCellDto>();
    for (const c of cells) m.set(c.kommunenummer, c);
    return m;
  }, [cells]);

  // Project + build the path strings once per geometry load (not per hover/zoom).
  const paths = useMemo(() => {
    if (!features) return [];
    const collection: FeatureCollection<Geometry, KommuneProps> = {
      type: 'FeatureCollection',
      features,
    };
    const projection = geoConicConformal()
      .rotate([-10.4, 0])
      .parallels([58, 72])
      .fitSize([W, H], collection);
    const path = geoPath(projection);
    return features.map((f: KommuneFeature) => ({
      code: kommuneCode(f),
      name: f.properties.name,
      d: path(f) ?? '',
    }));
  }, [features]);

  const onWheel = useCallback((e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const p = pointInSvg(e, e.currentTarget);
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setTransform((t) => zoomAround(t, factor, p.x, p.y));
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    drag.current = { x: e.clientX, y: e.clientY, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < 4) return;
    d.moved = true;
    d.x = e.clientX;
    d.y = e.clientY;
    const box = e.currentTarget.getBoundingClientRect();
    const sx = (dx / box.width) * W;
    const sy = (dy / box.height) * H;
    setHover(null);
    setTransform((t) => clampTransform({ ...t, x: t.x + sx, y: t.y + sy }));
  }, []);

  const endDrag = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (drag.current && e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    // Keep `moved` readable through the click that immediately follows pointerup.
    const wasDrag = drag.current?.moved ?? false;
    drag.current = wasDrag ? { x: 0, y: 0, moved: true } : null;
    if (wasDrag) setTimeout(() => (drag.current = null), 0);
  }, []);

  if (isLoading) return <SkeletonPanel lines={10} />;
  if (isError || !features) {
    return <p className="text-danger text-sm">Kunne ikke laste kartet.</p>;
  }

  const hoverCell = hover ? byCode.get(hover.code) : null;
  const hoverName = hover ? paths.find((p) => p.code === hover.code)?.name : null;
  const zoomed = transform.k > 1.001;

  return (
    <div className="space-y-3">
      <div className="border-hairline bg-base/20 relative overflow-hidden rounded-lg border">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className={cn('h-auto w-full touch-none select-none', zoomed ? 'cursor-grab' : '')}
          role="img"
          aria-label="Kommunekart over Norge"
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onMouseLeave={() => setHover(null)}
        >
          <g transform={`translate(${transform.x} ${transform.y}) scale(${transform.k})`}>
            {paths.map((p) => {
              const cell = byCode.get(p.code);
              const isSelected = cell && selectedId === cell.organizationId;
              return (
                <path
                  key={p.code}
                  d={p.d}
                  fill={cell && cell.status ? STATUS_FILL[cell.status] : EMPTY_FILL}
                  stroke={isSelected ? SELECTED_STROKE : MAP_STROKE}
                  strokeWidth={(isSelected ? 1.6 : 0.4) / transform.k}
                  className={cn(cell && 'cursor-pointer')}
                  style={{ transition: 'fill 120ms ease' }}
                  onMouseMove={(e) => {
                    if (drag.current?.moved) return;
                    const box = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
                    setHover({ code: p.code, x: e.clientX - box.left, y: e.clientY - box.top });
                  }}
                  onMouseEnter={(e) => {
                    if (drag.current?.moved) return;
                    const box = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
                    setHover({ code: p.code, x: e.clientX - box.left, y: e.clientY - box.top });
                  }}
                  onClick={() => {
                    if (drag.current?.moved) return;
                    if (cell) onSelect(cell);
                  }}
                />
              );
            })}
          </g>
        </svg>

        {/* Zoom controls */}
        <div className="absolute right-2 top-2 flex flex-col gap-1">
          <ZoomButton label="Zoom inn" onClick={() => setTransform((t) => zoomAround(t, 1.4, W / 2, H / 2))}>
            <Plus size={14} aria-hidden />
          </ZoomButton>
          <ZoomButton label="Zoom ut" onClick={() => setTransform((t) => zoomAround(t, 1 / 1.4, W / 2, H / 2))}>
            <Minus size={14} aria-hidden />
          </ZoomButton>
          <ZoomButton label="Tilbakestill" onClick={() => setTransform(IDENTITY)} disabled={!zoomed}>
            <RotateCcw size={13} aria-hidden />
          </ZoomButton>
        </div>

        {hover && (hoverCell || hoverName) && (
          <div
            className="border-hairline bg-panel pointer-events-none absolute z-20 max-w-[16rem] rounded-lg border p-2.5 text-xs shadow-xl"
            style={{
              left: Math.min(hover.x + 12, W - 40),
              top: hover.y + 12,
            }}
          >
            <p className="text-ink text-sm font-medium">{hoverCell?.name ?? hoverName}</p>
            {hoverCell ? (
              <>
                <p className="text-ink-soft/70 mb-1 text-[10px]">
                  {hoverCell.fylke ?? ''}
                  {hoverCell.population ? ` · ${formatNumber(hoverCell.population)} innb.` : ''}
                </p>
                {hoverCell.holdings.length > 0 ? (
                  <ul className="space-y-0.5">
                    {hoverCell.holdings.map((h) => (
                      <li key={h.productId} className="flex items-center gap-1.5">
                        <span
                          className="inline-block h-2 w-2 shrink-0 rounded-full"
                          style={{ background: STATUS_FILL[h.status] }}
                          aria-hidden
                        />
                        <span className="text-ink-soft">
                          <span className="text-ink">{h.productName}</span> ·{' '}
                          {PRODUCT_HOLDING_STATUS_LABELS[h.status]}
                          {h.competitor ? ` (${h.competitor})` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-ink-soft/70 text-[10px]">Ingen beholdning i utvalget — ikke kartlagt</p>
                )}
                <p className="text-accent mt-1 text-[10px]">Klikk for å åpne</p>
              </>
            ) : (
              <p className="text-ink-soft/70 text-[10px]">Ingen registrert beholdning i utvalget</p>
            )}
          </div>
        )}
      </div>

      <MapLegend cells={cells} />
    </div>
  );
}

function ZoomButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'border-hairline bg-panel/90 text-ink-soft hover:text-ink flex h-7 w-7 items-center justify-center rounded-md border shadow-sm transition-colors',
        disabled && 'cursor-not-allowed opacity-40 hover:text-ink-soft',
      )}
    >
      {children}
    </button>
  );
}

function MapLegend({ cells }: { cells: MapCellDto[] }) {
  const counts = new Map<string, number>();
  let neutral = 0;
  for (const c of cells) {
    if (c.status) counts.set(c.status, (counts.get(c.status) ?? 0) + 1);
    else neutral++;
  }
  return (
    <div className="border-hairline bg-base/20 rounded-lg border p-3">
      <p className="label-micro text-ink-soft mb-2">Status</p>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
        {MAP_LEGEND.map(({ status, label }) => (
          <li key={status} className="flex items-center justify-between gap-2 text-xs">
            <span className="flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ background: STATUS_FILL[status] }}
                aria-hidden
              />
              <span className="text-ink-soft">{label}</span>
            </span>
            <span className="text-ink-soft/60 tabular-nums">{counts.get(status) ?? 0}</span>
          </li>
        ))}
        <li className="flex items-center justify-between gap-2 text-xs">
          <span className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm" style={{ background: EMPTY_FILL }} aria-hidden />
            <span className="text-ink-soft/70">Ingen data</span>
          </span>
          <span className="text-ink-soft/60 tabular-nums">{neutral}</span>
        </li>
      </ul>
      <p className="text-ink-soft/60 mt-3 text-[10px] leading-relaxed">
        Hele landet vises. Fargen viser dominerende status når en kommune har flere produkter; grå kommuner er
        ikke kartlagt i utvalget. Hold over for alle beholdninger, klikk for å åpne.
      </p>
    </div>
  );
}
